import { zodResolver } from '@hookform/resolvers/zod';
import { JOB_MODALITY } from '@kiku/jobs/types';
import type { JobModality } from '@kiku/jobs/types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { InterviewFormatFields } from './interview-format-fields';

import { Button } from '~/components/ui/button';

const sharedSettingsSchema = z.object({
  modality: z.array(z.enum(JOB_MODALITY)).min(1),
  languages: z.array(z.string().length(2)).min(1)
});

type SharedSettings = z.infer<typeof sharedSettingsSchema>;

type SharedSettingsStepProps = {
  defaultValues?: Partial<SharedSettings>;
  onSubmit: (data: SharedSettings) => void;
  onBack: () => void;
  isSubmitting: boolean;
};

export function SharedSettingsStep({
  defaultValues,
  onSubmit,
  onBack,
  isSubmitting
}: SharedSettingsStepProps): React.ReactNode {
  const { t } = useTranslation();
  const {
    watch,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm<SharedSettings>({
    resolver: zodResolver(sharedSettingsSchema),
    defaultValues: { modality: ['text', 'voice'], languages: ['en'], ...defaultValues }
  });

  const selectedLanguages = watch('languages');
  const selectedModalities = watch('modality');

  function toggleModality(m: JobModality): void {
    const current = selectedModalities;
    if (current.includes(m)) {
      setValue(
        'modality',
        current.filter((v) => v !== m),
        { shouldValidate: true }
      );
    } else {
      setValue('modality', [...current, m], { shouldValidate: true });
    }
  }

  function toggleLanguage(code: string): void {
    const current = selectedLanguages;
    if (current.includes(code)) {
      setValue(
        'languages',
        current.filter((l: string) => l !== code),
        { shouldValidate: true }
      );
    } else {
      setValue('languages', [...current, code], { shouldValidate: true });
    }
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(onSubmit)(e)}
      className="flex flex-1 min-h-0 flex-col gap-6"
    >
      <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-y-auto">
        <p className="text-sm text-muted-foreground">{t('wizard.sharedSettings.subtitle')}</p>

        <InterviewFormatFields
          selectedModalities={selectedModalities}
          onModalityToggle={toggleModality}
          modalityError={!!errors.modality}
          selectedLanguages={selectedLanguages}
          onLanguageToggle={toggleLanguage}
          languagesError={!!errors.languages}
        />
      </div>

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack}>
          {t('wizard.back')}
        </Button>
        <Button
          type="submit"
          disabled={
            isSubmitting || selectedModalities.length === 0 || selectedLanguages.length === 0
          }
        >
          {t('wizard.next')}
        </Button>
      </div>
    </form>
  );
}
